import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, FileImage, Download, Trash2, UploadCloud, File as FileIcon } from "lucide-react";
import { DOCUMENT_CATEGORIES } from "@/config/documentOptions";
import { VISIBILITIES, optOf, labelOf, TONE } from "@/config/commOptions";
import { toast } from "sonner";

const MetaPill = ({ arr, value, testid }) => {
  if (!value) return null;
  const o = optOf(arr, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold border"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>
      {o?.label || labelOf(arr, value)}
    </span>
  );
};

const iconFor = (ct) => {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct.includes("pdf")) return FileText;
  return FileIcon;
};
const prettySize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function DocumentsPanel({ relatedType, relatedId, branchId }) {
  const { user } = useAuth();
  const canWrite = !user?.is_portal;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("other");
  const [visibility, setVisibility] = useState("internal");
  const inputRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = relatedId ? { related_type: relatedType, related_id: relatedId } : {};
      const { data } = await api.get("/documents", { params });
      setDocs(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [relatedId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (relatedType) fd.append("related_type", relatedType);
    if (relatedId) fd.append("related_id", relatedId);
    if (branchId) fd.append("branch_id", branchId);
    fd.append("category", category);
    fd.append("visibility", visibility);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const open = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open file");
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9" data-testid="doc-upload-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">{DOCUMENT_CATEGORIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="h-9" data-testid="doc-upload-visibility"><SelectValue placeholder="Visibility" /></SelectTrigger>
              <SelectContent>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div
            onClick={() => inputRef.current?.click()}
            data-testid="documents-upload-dropzone"
            className="cursor-pointer rounded-xl border-2 border-dashed border-[#9BB9CE] bg-[rgba(155,185,206,0.08)] hover:bg-[rgba(155,185,206,0.16)] transition-colors p-5 text-center"
          >
            <UploadCloud className="mx-auto mb-1 text-[#1C4358]" size={26} />
            <p className="text-sm font-medium text-[#1C4358]">{uploading ? "Uploading..." : "Click to upload a document"}</p>
            <p className="text-xs text-muted-foreground">PDF, images, Word, Excel · up to 25MB</p>
            <input ref={inputRef} type="file" className="hidden" onChange={onFile} data-testid="document-file-input" />
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No documents yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {docs.map((d) => {
            const I = iconFor(d.content_type);
            return (
              <div key={d.id} className="flex items-center gap-3 rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="document-card">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
                  <I size={20} className="text-[#1C4358]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate text-[#252221]">{d.original_filename || d.file_name}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <MetaPill arr={DOCUMENT_CATEGORIES} value={d.category} testid="doc-card-category" />
                    <MetaPill arr={VISIBILITIES} value={d.visibility} testid="doc-card-visibility" />
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">{prettySize(d.size)} · {formatDate(d.created_at)}</div>
                </div>
                <button onClick={() => open(d)} className="p-2 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]" data-testid="document-download-button" title="Open">
                  <Download size={16} />
                </button>
                {canWrite && (
                  <button onClick={() => remove(d.id)} className="p-2 rounded-lg hover:bg-[rgba(156,30,34,0.08)] text-[#9C1E22]" data-testid="document-delete-button" title="Delete">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
